import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { fetchDownloadDicomSeries } from '~/backend/connector-api/connector-components';
import { Modality, strToModality } from '~/domain/dicom/modality';
import { Image3D } from '~/domain/patient/image3d';
import {
    blendImageState,
    primaryImageState,
} from '~/state/atoms/image/imageAtoms';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { loadingOverlayState } from '~/state/atoms/viewerAtoms';
import useSelectedPatient from '../patient/useSelectedPatient';
import useProcessDicomFiles from '../useProcessDicomFiles';
import useViewportEnvironment from '../useViewportEnvironment';

export type AvailableImage = {
    seriesInstanceUID: string;
    studyInstanceUID: string;
    label: string;
    modality: Modality;
    isLoaded: boolean;
    imageObject?: Image3D; // Only present if loaded
};

export default function usePrimaryImage(showSelected = false) {
    const [availableImages, setAvailableImages] = useState<AvailableImage[]>(
        []
    );
    const patient = useSelectedPatient();
    const sessionData = useRecoilValue(sessionDataAtom);
    const primaryImage = useRecoilValue(primaryImageState);
    const blendImage = useRecoilValue(blendImageState);
    const { setEnvironmentPrimaryImage, initializeEnvironment } =
        useViewportEnvironment();
    const setLoadingOverlay = useSetRecoilState(loadingOverlayState);
    const { processImportedFiles } = useProcessDicomFiles();

    useEffect(() => {
        const combinedImages: AvailableImage[] = [];

        // Add loaded images (from patient)
        const loadedImages = patient
            ? Array.from(patient.images)
                  .filter(
                      (img) =>
                          showSelected ||
                          img.seriesUid !== primaryImage?.seriesUid
                  )
                  .map((img) => ({
                      seriesInstanceUID: img.seriesUid,
                      studyInstanceUID: img.studyUid,
                      label: img.seriesMetadata.seriesDescription,
                      modality: img.seriesMetadata.modality,
                      isLoaded: true,
                      imageObject: img,
                  }))
            : [];
        combinedImages.push(...loadedImages);

        // Add session images (not loaded)
        if (sessionData?.payload?.images) {
            const sessionImages = sessionData.payload.images
                .filter(
                    (img) =>
                        img &&
                        !loadedImages.some(
                            (loadedImg) =>
                                loadedImg.seriesInstanceUID ===
                                img.series_instance_uid
                        )
                )
                .map((img) => ({
                    seriesInstanceUID: img.series_instance_uid!,
                    studyInstanceUID: img.study_instance_uid!,
                    modality: strToModality(img.modality ?? ''),
                    label: img.display_name ?? '',
                    isLoaded: false,
                }));
            combinedImages.push(...sessionImages);
        }

        setAvailableImages(combinedImages);
    }, [patient, primaryImage, blendImage, sessionData]);

    const handleChangePrimaryImage = useCallback(
        async (image: AvailableImage) => {
            if (image.imageObject && patient) {
                setEnvironmentPrimaryImage(
                    [image.imageObject],
                    patient?.dataSet.PatientID
                );
            } else if (!image.isLoaded && sessionData?.payload?.patient?.id) {
                setLoadingOverlay({
                    isOpen: true,
                    message: 'Downloading images...',
                });
                const dicomBlob = await fetchDownloadDicomSeries({
                    pathParams: {
                        studyId: image.studyInstanceUID,
                        seriesId: image.seriesInstanceUID,
                    },
                    queryParams: { series: true },
                });
                const files = [
                    new File([dicomBlob], `${image.seriesInstanceUID}.zip`),
                ];

                const processedItems = await processImportedFiles(files);
                setLoadingOverlay({
                    isOpen: false,
                });
                if (processedItems) {
                    initializeEnvironment(processedItems);
                }
            }
        },
        [patient, sessionData]
    );

    return { primaryImage, availableImages, handleChangePrimaryImage };
}
